import { Controller } from '@hotwired/stimulus'

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ')

// Remembers which element opened a given modal, so that the focus can be given back when it closes.
const openers = new WeakMap()

export default class extends Controller {
  static values = {autoShow: Boolean, targetId: String}

  connect() {
    if (this.autoShowValue) { this.open() }
  }

  disconnect() {
    openers.delete(this.element)
  }

  open() {
    const modal = this.hasTargetIdValue ? document.getElementById(this.targetIdValue) : this.element

    if (modal === null) { return }

    if (modal !== this.element) { openers.set(modal, this.element) }

    modal.classList.remove('hidden')

    const focusableElements = this.#focusableElementsIn(modal)
    const elementToFocus = focusableElements.length > 0 ? focusableElements[0] : modal

    elementToFocus.focus()
  }

  close() {
    this.element.classList.add('hidden')

    const opener = openers.get(this.element)
    openers.delete(this.element)

    if (opener) { opener.focus() }
  }

  closeOnEscape(event) {
    if (event.key !== 'Escape') { return }

    this.close()
  }

  // The controller element is the overlay: a click on it happened outside of the dialog itself.
  closeOnOutsideClick(event) {
    if (event.target !== this.element) { return }

    this.close()
  }

  // Keeps the tabbing sequence inside the modal as long as it is open.
  confineFocus(event) {
    if (event.key !== 'Tab') { return }

    const focusableElements = this.#focusableElementsIn(this.element)

    if (focusableElements.length === 0) { return }

    const firstElement = focusableElements[0]
    const lastElement = focusableElements[focusableElements.length - 1]

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault()
      lastElement.focus()
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault()
      firstElement.focus()
    }
  }

  #focusableElementsIn(element) {
    return Array.from(element.querySelectorAll(FOCUSABLE_SELECTOR))
  }
}
